// 用户服务工具函数
// 用于异步调用用户信息API，避免阻塞主线程

import { createClient } from "@/utils/supabase/client";
import { logger } from "@/lib/logger";

// 防重复调用的状态管理
let isFetching = false;
let fetchPromise = null;

// 获取用户IP地址
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    logger.warn('Failed to get user IP:', error);
    return 'unknown';
  }
};

// 🔥 优化：移除getUserLocation，因为中间件已经提供了国家信息
// 中间件通过 Cloudflare/Vercel headers 获取国家信息，更高效且无需额外API调用

// 获取用户设备信息
const getUserDeviceInfo = () => {
  if (typeof window === 'undefined') {
    return {
      device_type: 'unknown',
      user_agent: 'unknown'
    };
  }

  const userAgent = navigator.userAgent;
  let deviceType = 'desktop';

  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    deviceType = 'mobile';
  } else if (/iPad/i.test(userAgent)) {
    deviceType = 'tablet';
  }

  return {
    device_type: deviceType,
    user_agent: userAgent
  };
};

// 获取UTM参数
const getUTMParams = () => {
  if (typeof window === 'undefined') {
    return {
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: ''
    };
  }

  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || ''
  };
};

// 获取referrer信息
const getReferrerInfo = () => {
  if (typeof window === 'undefined') {
    return {
      referrer: '',
      referrer_domain: ''
    };
  }

  const referrer = document.referrer || '';
  const referrerDomain = referrer ? new URL(referrer).hostname : '';

  return {
    referrer,
    referrer_domain: referrerDomain
  };
};

// 🔥 优化：从cookie获取中间件提供的国家信息
const getCountryFromCookie = () => {
  if (typeof window === 'undefined') {
    return {
      user_country: 'unknown',
      user_country_code: 'unknown'
    };
  }

  try {
    const userDataCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('user_data='));
    
    if (userDataCookie) {
      const userData = JSON.parse(decodeURIComponent(userDataCookie.split('=')[1]));
      return {
        user_country: userData.user_country || 'unknown',
        user_country_code: userData.user_country_code || 'unknown'
      };
    }
  } catch (error) {
    logger.warn('Failed to parse user data cookie:', error);
  }

  return {
    user_country: 'unknown',
    user_country_code: 'unknown'
  };
};

// 构建用户数据
const buildUserData = async () => {
  try {
    // 🔥 优化：移除getUserLocation调用，使用cookie中的国家信息
    const [ip, deviceInfo, utmParams, referrerInfo] = await Promise.allSettled([
      getUserIP(),
      Promise.resolve(getUserDeviceInfo()),
      Promise.resolve(getUTMParams()),
      Promise.resolve(getReferrerInfo())
    ]);

    // 从cookie获取国家信息
    const countryInfo = getCountryFromCookie();

    return {
      user_ip: ip.status === 'fulfilled' ? ip.value : 'unknown',
      user_country: countryInfo.user_country,
      user_country_code: countryInfo.user_country_code,
      device_type: deviceInfo.status === 'fulfilled' ? deviceInfo.value.device_type : 'unknown',
      user_agent: deviceInfo.status === 'fulfilled' ? deviceInfo.value.user_agent : 'unknown',
      utm_source: utmParams.status === 'fulfilled' ? utmParams.value.utm_source : '',
      utm_medium: utmParams.status === 'fulfilled' ? utmParams.value.utm_medium : '',
      utm_campaign: utmParams.status === 'fulfilled' ? utmParams.value.utm_campaign : '',
      utm_term: utmParams.status === 'fulfilled' ? utmParams.value.utm_term : '',
      utm_content: utmParams.status === 'fulfilled' ? utmParams.value.utm_content : '',
      referrer: referrerInfo.status === 'fulfilled' ? referrerInfo.value.referrer : '',
      referrer_domain: referrerInfo.status === 'fulfilled' ? referrerInfo.value.referrer_domain : ''
    };
  } catch (error) {
    logger.error('Error building user data:', error);
    return {
      user_ip: 'unknown',
      user_country: 'unknown',
      user_country_code: 'unknown',
      device_type: 'unknown',
      user_agent: 'unknown',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      referrer: '',
      referrer_domain: ''
    };
  }
};

// 获取用户信息
export const fetchUserInfo = async () => {
  // 防重复调用：如果正在获取中，返回现有的Promise
  if (isFetching && fetchPromise) {
    logger.info('[UserService] Request already in progress, returning existing promise');
    return fetchPromise;
  }

  // 创建新的获取请求
  isFetching = true;
  fetchPromise = (async () => {
    try {
      logger.info('[UserService] Fetching user info from API...');
      
      const userData = await buildUserData();
      
      // 获取认证token
      let authToken = null;
      try {
        const supabase = createClient();
        const { data: { session } } = await supabase.auth.getSession();
        authToken = session?.access_token;
      } catch (error) {
        logger.warn('[UserService] Failed to get auth token:', error);
      }
      
      // 构建请求头
      const headers = {
        'Content-Type': 'application/json',
        'X-Forwarded-For': userData.user_ip,
        'X-Real-IP': userData.user_ip,
        'X-User-Country': userData.user_country,
        'X-User-Country-Code': userData.user_country_code,
        'X-Device-Type': userData.device_type,
        'X-User-Agent': userData.user_agent,
        'X-UTM-Data': userData.utm_data,
        'X-Referer': userData.referer,
        'X-Referrer-Domain': userData.referrer_domain
      };

      // 添加认证token
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      logger.info('[UserService] Making API request to /api/user/profile');
      
      const response = await fetch('/api/user/profile', {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      logger.info('[UserService] API response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[UserService] API response error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          headers: Object.fromEntries(response.headers.entries())
        });
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      
      if (data.error) {
        logger.error('[UserService] API returned error:', data.error);
        throw new Error(data.error);
      }

      logger.info('[UserService] User info fetched successfully');
      return data;

    } catch (error) {
      logger.error('[UserService] Failed to fetch user info:', error);
      throw error;
    } finally {
      // 重置状态
      isFetching = false;
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

// 更新用户信息
export const updateUserInfo = async (userData) => {
  try {
    logger.info('[UserService] Updating user info...');
    
    const response = await fetch('/api/user/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }

    logger.info('[UserService] User info updated successfully');
    return data;

  } catch (error) {
    logger.error('[UserService] Failed to update user info:', error);
    throw error;
  }
};

// 延迟获取用户信息（用于避免阻塞首屏渲染）
export const fetchUserInfoDelayed = (delay = 1000) => {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        const result = await fetchUserInfo();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }, delay);
  });
};

// 预加载用户信息（在后台静默加载）
export const preloadUserInfo = () => {
  if (typeof window === 'undefined') return;
  
  // 使用 requestIdleCallback 在浏览器空闲时加载
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      fetchUserInfo().catch(error => {
        logger.warn('[UserService] Preload failed:', error);
      });
    });
  } else {
    // 回退到 setTimeout
    setTimeout(() => {
      fetchUserInfo().catch(error => {
        logger.warn('[UserService] Preload failed:', error);
      });
    }, 2000);
  }
}; 